import { type InlineChipColor } from '@precis-digital/kurama/InlineChip'
import { STATUSES } from 'shared/configStatuses'
import { PLATFORM } from 'src/dataSource/types'
import { type Platforms } from 'shared/api/accounts'
import { type FlowSkillKnowledgeCorpus, type FlowSkillListItem, type FlowSkillScope } from 'shared/api/copilot'
import { type Skill, type SkillDataSource, type SkillKnowledgeSource, type SkillScope } from 'src/copilot/types/skills'

/** Matches Flow `DataSourceCategory` / UI `ALL_SOURCES`. */
export type FlowDataSourceCategory =
  | 'ga4'
  | 'gads'
  | 'meta'
  | 'linkedin'
  | 'tiktok'
  | 'snapchat'
  | 'pinterest'
  | 'alvie_starter'
  | 'bq'
  | 'search_console'
  | 'merchant_center'

const CATEGORY_TO_PLATFORM: Record<FlowDataSourceCategory, Platforms> = {
  ga4: PLATFORM.GA4,
  gads: PLATFORM.GOOGLE,
  meta: PLATFORM.FACEBOOK,
  linkedin: PLATFORM.LINKEDIN,
  tiktok: PLATFORM.TIKTOK,
  snapchat: PLATFORM.SNAPCHAT,
  pinterest: PLATFORM.PINTEREST,
  search_console: PLATFORM.GOOGLE_SEARCH_CONSOLE,
  alvie_starter: PLATFORM.GCP,
  bq: PLATFORM.GOOGLE_BIGQUERY,
  merchant_center: PLATFORM.MERCHANT_CENTER
}

const CATEGORY_LABEL: Record<FlowDataSourceCategory, string> = {
  ga4: 'Google Analytics 4',
  gads: 'Google Ads',
  meta: 'Meta Ads',
  linkedin: 'LinkedIn Ads',
  tiktok: 'TikTok Ads',
  snapchat: 'Snapchat Ads',
  pinterest: 'Pinterest Ads',
  search_console: 'Google Search Console',
  alvie_starter: 'Alvie Starter Report',
  bq: 'BigQuery',
  merchant_center: 'Merchant Center'
}

/** Flow wire scope → Alvie Skills overview / domain model. */
export const mapFlowSkillScopeToAlvie = (scope: FlowSkillScope): SkillScope => {
  switch (scope) {
    case 'PRIVATE':
      return 'personal'
    case 'ORGANIZATION':
      return 'organisation'
    case 'WORKSPACE':
    default:
      return 'workspace'
  }
}

/** Alvie domain scope → Flow `<skill scope="…">` / API. */
export const mapAlvieSkillScopeToFlow = (scope: SkillScope): FlowSkillScope => {
  switch (scope) {
    case 'personal':
      return 'PRIVATE'
    case 'organisation':
      return 'ORGANIZATION'
    case 'workspace':
    default:
      return 'WORKSPACE'
  }
}

/** Skill scope → Kurama `InlineChip` colour (composer chips and transcript pills). */
export const getSkillChipColor = (scope: SkillScope): InlineChipColor => {
  switch (scope) {
    case 'personal':
      return 'violet'
    case 'organisation':
      return 'teal'
    case 'workspace':
    default:
      return 'precis-blue'
  }
}

/** Flow wire scope → Kurama `InlineChip` colour. */
export const getFlowSkillChipColor = (scope: FlowSkillScope): InlineChipColor => {
  return getSkillChipColor(mapFlowSkillScopeToAlvie(scope))
}

/** Parse a Flow scope attribute; unknown values default to WORKSPACE. */
export const parseFlowSkillScope = (raw: string | undefined): FlowSkillScope => {
  const scope = raw?.trim().toUpperCase()
  if (scope === 'PRIVATE' || scope === 'WORKSPACE' || scope === 'ORGANIZATION') {
    return scope
  }
  return 'WORKSPACE'
}

const isFlowDataSourceCategory = (category: string): category is FlowDataSourceCategory =>
  Object.hasOwn(CATEGORY_TO_PLATFORM, category)

/**
 * Unknown Flow categories are dropped: without a platform mapping we cannot check
 * whether the data source is connected, so they must not block skill selection.
 */
const mapDataSources = (categories: string[] | undefined): SkillDataSource[] => {
  if (categories == null || categories.length === 0) {
    return []
  }

  return categories.filter(isFlowDataSourceCategory).map((category) => ({
    id: category,
    name: CATEGORY_LABEL[category],
    platform: CATEGORY_TO_PLATFORM[category]
  }))
}

const mapKnowledgeSources = (corpora: FlowSkillKnowledgeCorpus[] | undefined): SkillKnowledgeSource[] => {
  if (corpora == null || corpora.length === 0) {
    return []
  }

  return corpora.map((corpus) => ({
    id: corpus.vertexCorpusId,
    name: corpus.name
  }))
}

/** Map a Flow skill list item into the Alvie Skills overview row model. */
export const mapFlowSkillListItemToSkill = (item: FlowSkillListItem): Skill => {
  const updatedBy = item.flowUpdatedBy
  return {
    id: String(item.id),
    name: item.title !== '' ? item.title : item.name,
    flowName: item.name,
    description: item.description ?? '',
    status: item.enabled === false ? STATUSES.paused : STATUSES.active,
    scope: mapFlowSkillScopeToAlvie(item.scope),
    dataSources: mapDataSources(item.dataSourceCategory),
    knowledgeSources: mapKnowledgeSources(item.knowledgeCorpora),
    updatedAt: item.updatedAt,
    updatedByEmail: updatedBy?.email,
    updatedByMemberId: updatedBy?.alvieId != null ? String(updatedBy.alvieId) : undefined
  }
}

export const mapFlowSkillListToSkills = (items: FlowSkillListItem[]): Skill[] => {
  return items.map(mapFlowSkillListItemToSkill)
}
